import { LucideIcon } from "lucide-react";
import { providerRegistry } from "./auth/oauth/providerRegistry";
import { initializeOAuthProviders } from "./auth/oauth/providerFactory";

// Initialize providers from environment on module load
initializeOAuthProviders();

/**
 * UI configuration for OAuth providers
 * This is separate from the OAuth logic and is used for display purposes
 */
export interface OAuthProviderUI {
  id: string;
  name: string;
  icon?: LucideIcon | React.ComponentType;
  brandColor: string;
  textColor?: string;
}

/**
 * UI configurations for each provider
 */
const providerUIConfigs: Record<string, OAuthProviderUI> = {
  google: {
    id: 'google',
    name: 'Google',
    brandColor: '#4285F4',
    textColor: '#FFFFFF',
  },
  // Future providers can be added here:
  // github: {
  //   id: 'github',
  //   name: 'GitHub',
  //   brandColor: '#24292e',
  //   textColor: '#FFFFFF',
  // },
  // facebook: {
  //   id: 'facebook',
  //   name: 'Facebook',
  //   brandColor: '#1877F2',
  //   textColor: '#FFFFFF',
  // },
  // microsoft: {
  //   id: 'microsoft',
  //   name: 'Microsoft',
  //   brandColor: '#0078D4',
  //   textColor: '#FFFFFF',
  // },
};

/**
 * Get UI configuration for a specific OAuth provider
 */
export function getProviderUIConfig(providerId: string): OAuthProviderUI | undefined {
  return providerUIConfigs[providerId];
}

/**
 * Get all available OAuth providers (combines registry and UI config)
 */
export function getAvailableProviders(): Array<OAuthProviderUI & { enabled: boolean }> {
  const registeredProviders = providerRegistry.getAllProviders();
  
  return registeredProviders
    .map(provider => {
      const uiConfig = providerUIConfigs[provider.id];
      if (!uiConfig) {
        console.warn(`UI config not found for provider: ${provider.id}`);
        return null;
      }
      return {
        ...uiConfig,
        enabled: true,
      };
    })
    .filter((p): p is OAuthProviderUI & { enabled: boolean } => p !== null);
}

/**
 * Check if a provider is enabled
 */
export function isProviderEnabled(providerId: string): boolean {
  return providerRegistry.hasProvider(providerId);
}

/**
 * Get configuration for a specific OAuth provider (legacy compatibility)
 */
export function getProviderConfig(providerId: string): (OAuthProviderUI & { enabled: boolean }) | undefined {
  const provider = providerRegistry.getProvider(providerId);
  const uiConfig = providerUIConfigs[providerId];
  
  if (!provider || !uiConfig) {
    return undefined;
  }
  
  return {
    ...uiConfig,
    enabled: true,
  };
}

// Legacy export for backwards compatibility
export type OAuthProvider = OAuthProviderUI & { enabled: boolean };
